import i18n from '../i18n/i18n';

/**
 * 將後端 notification 的 event_type + content 解析為翻譯後的 title / description
 * @param {Object} notification - { event_type, content }
 * @returns {{ title: string, description: string }}
 */
export function resolveNotification(notification) {
  const { event_type, content = {} } = notification;

  if (!event_type) {
    return { title: '', description: '' };
  }

  // 翻譯 content 中的 platform 和 action
  const resolvedContent = { ...content };
  if (resolvedContent.platform) {
    resolvedContent.platform = i18n.t(
      `enums.platform.${resolvedContent.platform}`,
      { defaultValue: resolvedContent.platform }
    );
  }

  // 把 interpolations（後端依 AppErrors::Error / Platforms::Errors 的 .interpolations
  // 注入的動態變數，例如 {sku: 'ABC'}）flatten 進 t() params，讓 error_codes.<code>
  // template 可以直接引用 {{sku}}、{{platform_code}} 等變數，提供「哪個 SKU 沒對到」
  // 這類上下文。後端遵守「不返翻譯字串」契約，所以前端負責拼。
  const interpolations = resolvedContent.interpolations || {};

  // 後端遵守「只回 error code，不返翻譯字串」契約（BACKEND.md），content.error 可能是
  // snake_case 代碼（例：'sku_not_found_strict'）也可能是原樣 message（例外鏈 e.message
  // 透傳）。先試 i18n 查表把代碼翻成人話；查不到就 defaultValue 回原值，不顯示「翻譯
  // 失敗」這類 noisy 文字。**只用 t 自身的 defaultValue fallback，不要用 includes('.')
  // 或 'missing' 字串去 sniff 結果**——i18n 對 missing key 在 dev 環境會回 default、
  // 在 prod 環境視設定可能直接回 key 本身，sniff 很容易踩錯。
  if (resolvedContent.error) {
    resolvedContent.error = i18n.t(
      `notifications.error_codes.${resolvedContent.error}`,
      { ...resolvedContent, ...interpolations, defaultValue: resolvedContent.error }
    );
  }
  const title = i18n.t(
    `notifications.events.${event_type}.title`,
    { ...resolvedContent, defaultValue: event_type }
  );

  const description = i18n.t(
    `notifications.events.${event_type}.description`,
    { ...resolvedContent, defaultValue: '' }
  );

  return { title, description };
}

/**
 * 判斷是否需要自訂渲染元件（例如到期通知有 items 列表）
 * @param {string} event_type
 * @returns {boolean}
 */
export function hasCustomRenderer(event_type) {
  return ['expiration_notification', 'platform_missing_customs_declaration'].includes(event_type);
}

/**
 * 根據 notifiable_type 和 notifiable_id 產生前端跳轉路徑
 * @param {Object} notification - { notifiable_type, notifiable_id }
 * @param {string} role - 'admin' | 'operator'
 * @returns {string|null}
 */
export function getNotifiablePath(notification, role = 'admin') {
  const { notifiable_type, notifiable_id } = notification;
  if (!notifiable_type || !notifiable_id) return null;

  const pathMap = {
    Order: `/orders/${notifiable_id}`,
    Fulfillment: `/fulfillments/${notifiable_id}`,
    EcAccount: `/ec_accounts/${notifiable_id}`,
    MerchantInventoryType: `/inventories`,
  };

  return pathMap[notifiable_type] || null;
}
